import React, { useContext, useEffect, useRef, useState } from "react";
import { DocxEditor, DocxEditorRef } from "@eigenpal/docx-js-editor";
import "@eigenpal/docx-js-editor/styles.css";
import { Button, Card, CardBody, Col } from "reactstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import MenuTop from "../../components/MenuTop/MenuTop";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import useTemplates from "../../context/hooks/useTemplates";
import InputStandard from "../../components/Inputs/InputStandard";
import { LoadingContext } from "../../context/LoadingContext";
import "./DocxEditorView.css";

const DocxEditorView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editorRef = useRef<DocxEditorRef>(null);
  const { loading } = useContext(LoadingContext);
  const { saveDocxTemplate, loadDocxEditorContext } = useTemplates();

  const [documentBuffer, setDocumentBuffer] = useState<ArrayBuffer | null>(
    null
  );
  const [nameTemplate, setNameTemplate] = useState("");
  const [fileName, setFileName] = useState("");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const idFromUrl = searchParams.get("id");

  useEffect(() => {
    const loadExisting = async () => {
      if (!idFromUrl) {
        setInitialLoadDone(true);
        return;
      }
      setTemplateId(idFromUrl);
      const data = await loadDocxEditorContext(idFromUrl);
      if (data?.meta?.nameTemplate) {
        setNameTemplate(data.meta.nameTemplate);
      }
      if (data?.buffer && data.buffer.byteLength > 0) {
        setFileName("template.docx");
        setDocumentBuffer(data.buffer);
      }
      setInitialLoadDone(true);
    };
    loadExisting();
    // loadDocxEditorContext é recriado a cada render; id basta para recarregar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idFromUrl]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const buffer = e.target?.result as ArrayBuffer;
        setDocumentBuffer(buffer);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleSave = async () => {
    if (!documentBuffer) {
      alert("Por favor, faça upload de um documento DOCX primeiro.");
      return;
    }
    if (!nameTemplate.trim()) {
      alert("Informe o nome do template.");
      return;
    }

    try {
      let blob: Blob;
      if (editorRef.current) {
        const saved = await editorRef.current.save();
        if (saved && saved.byteLength > 0) {
          blob = new Blob([saved], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });
        } else {
          blob = new Blob([documentBuffer], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });
        }
      } else {
        blob = new Blob([documentBuffer], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
      }

      const ok = await saveDocxTemplate(
        nameTemplate.trim(),
        blob,
        fileName || "template.docx",
        templateId || undefined
      );
      if (ok) {
        navigate("/admin/template");
      }
    } catch (error) {
      console.error("Erro ao salvar template:", error);
      alert("Erro ao salvar o template. Tente novamente.");
    }
  };

  const showUploadZone = !idFromUrl
    ? !documentBuffer
    : initialLoadDone && !documentBuffer;

  return (
    <>
      <MenuTop />
      <div
        className="mt-3 ml-3 mr-2 pt-4 pb-4 pl-4 docx-editor-page"
        style={{
          backgroundColor: "#fff",
          borderRadius: "5px",
          minHeight: "calc(100vh - 100px)",
        }}
      >
        <div className="docx-editor-topbar d-flex justify-content-between align-items-center flex-wrap mb-3">
          <span className="title-page-document mb-0">EDITOR DOCX</span>
          <div className="docx-editor-topbar-actions d-flex align-items-center flex-wrap">
            <Button
              className="button-login docx-editor-save-btn mr-2 mb-2 mb-md-0"
              onClick={handleSave}
              disabled={loading || (Boolean(idFromUrl) && !initialLoadDone)}
            >
              Salvar template
            </Button>
            <Button
              className="secondary mb-2 mb-md-0"
              onClick={() => navigate("/admin/template")}
              disabled={loading}
            >
              Voltar
            </Button>
          </div>
        </div>

        <div className="mb-3">
          <Col xs="12" md="6" className="p-0">
            <InputStandard
              type="text"
              name="nameTemplate"
              handleChange={(valueAtt: string) => setNameTemplate(valueAtt)}
              value={nameTemplate}
              label="Nome do Template"
            />
          </Col>
        </div>

        <details className="mb-3 docx-editor-help">
  <summary className="docx-editor-help-summary">
    ℹ️ Como usar variáveis no seu template DOCX
  </summary>
  <div className="docx-editor-help-content">
    <p>
      Você pode usar <strong>campos dinâmicos</strong> dentro do seu
      documento Word para gerar contratos personalizados automaticamente.
      Para isso, digite <strong>duas chaves no início e duas chaves no
      fim</strong> da palavra, assim:
    </p>
    <code>{"{{"}NOME{"}}"}</code>
    <p className="mt-2">
      Durante o uso do template, esse texto será substituído pelo{" "}
      <strong>nome real da pessoa</strong>.
    </p>
    <p>Outros exemplos de campos que você pode usar:</p>
    <ul>
      <li><code>{"{{"}ENDERECO{"}}"}</code> → será substituído pelo endereço</li>
      <li><code>{"{{"}CPF{"}}"}</code> → será substituído pelo CPF</li>
      <li><code>{"{{"}DATA{"}}"}</code> → será substituído pela data atual</li>
    </ul>

    <hr />

    <p>
      <strong>Posicionar assinaturas no documento</strong>
    </p>
    <p>
      Para definir <strong>onde</strong> cada signatário deve assinar,
      escreva no DOCX, exatamente no local desejado, as tags{" "}
      <code>{"{{"}ASSINATURA1{"}}"}</code>,{" "}
      <code>{"{{"}ASSINATURA2{"}}"}</code>, ... numeradas em sequência.
      A posição (página, X e Y) de cada tag é detectada automaticamente
      ao usar o modelo, e a tag é removida do PDF final.
    </p>
    <ul>
      <li>
        Use uma tag por signatário, sem repetir nem pular números (ex.:{" "}
        <code>{"{{"}ASSINATURA1{"}}"}</code> e{" "}
        <code>{"{{"}ASSINATURA2{"}}"}</code>, nunca{" "}
        <code>{"{{"}ASSINATURA1{"}}"}</code> e{" "}
        <code>{"{{"}ASSINATURA3{"}}"}</code>).
      </li>
      <li>
        A ordem dos signatários adicionados na próxima etapa segue a
        numeração das tags: o 1º signatário será associado a{" "}
        <code>{"{{"}ASSINATURA1{"}}"}</code>, o 2º a{" "}
        <code>{"{{"}ASSINATURA2{"}}"}</code>, e assim por diante.
      </li>
      <li>
        Se o modelo tiver tags de assinatura, será obrigatório informar
        exatamente o mesmo número de signatários ao usar o modelo —
        tanto pelo painel quanto pela API externa.
      </li>
      <li>
        Dica: deixe a linha da assinatura em branco e coloque a tag
        sozinha, no tamanho e posição em que a assinatura deve aparecer.
      </li>
    </ul>
  </div>
</details>

        {Boolean(idFromUrl) && !initialLoadDone && (
          <p className="text-muted">Carregando template...</p>
        )}

        {showUploadZone ? (
          <div className="upload-section">
            <Card style={{ border: "2px dashed #33BBED", backgroundColor: "#f8f9fa" }}>
              <CardBody className="text-center p-5">
                <CloudUploadOutlinedIcon
                  style={{ fontSize: "60px", color: "#33BBED" }}
                />
                <h4 className="mt-3 mb-3">Faça upload de um arquivo DOCX</h4>
                <input
                  type="file"
                  accept=".docx"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                  id="docx-upload"
                />
                <label htmlFor="docx-upload">
                  <Button className="button-login" tag="span">
                    Selecionar Arquivo
                  </Button>
                </label>
              </CardBody>
            </Card>
          </div>
        ) : (
          !Boolean(idFromUrl) || initialLoadDone ? (
          <div className="editor-section">
            <div className="mb-3">
              <strong>Arquivo:</strong> {fileName}
              <Button
                color="link"
                size="sm"
                onClick={() => {
                  setDocumentBuffer(null);
                  setFileName("");
                }}
              >
                Trocar arquivo
              </Button>
            </div>

            <div className="docx-editor-container">
              <DocxEditor
                key={`${templateId ?? "new"}-${fileName}`}
                ref={editorRef}
                documentBuffer={documentBuffer!}
                mode="editing"
                onChange={() => {}}
              />
            </div>
          </div>
          ) : null
        )}
      </div>
    </>
  );
};

export default DocxEditorView;
